import { useMemo, useState } from "react";
import { Info, Plus, Printer } from "lucide-react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Textarea,
  Tooltip,
} from "@heroui/react";
import { SheetSection } from "./sheet-primitives";
import { StatusBadge, type StatusTone } from "@/components/status-badge";
import { useToast } from "@/hooks/use-toast";
import {
  isConflictError,
  type PickupBookingDerivedState,
  useCancelPickupBooking,
  useDetachShipmentFromPickupBooking,
  useMyPickupBookings,
  usePrintPickupBooking,
  useUpdatePickupBookingNotes,
} from "@/api/use-my-pickup-bookings";
import { SchedulePickupSheet } from "./schedule-pickup-sheet";
import { AttachToBookingModal } from "./attach-to-booking-modal";
import { PickupRescheduleDialog } from "./pickup-reschedule-dialog";
import {
  formatDateInTz,
  formatSlotRangeInTz,
  todayIsoInTz,
  todayPlusDaysIsoInTz,
} from "@/lib/dispatch-time";
import { useTranslation } from "react-i18next";

/**
 * Phase 11a: customer-side detail sheet for a single PickupBooking.
 *
 * Lists attached shipments, editable notes, detach + cancel actions, print.
 * Driven by the bookingId prop. Closes via onClose.
 */

interface Props {
  bookingId: string | null;
  onClose: () => void;
}

const NOTES_MAX_LENGTH = 500;

function formatDateLong(
  iso: string,
  timeZone: string,
  t: (key: string) => string,
  locale?: string
): string {
  const date = iso.slice(0, 10);
  const todayDate = todayIsoInTz(timeZone);
  const tomorrowDate = todayPlusDaysIsoInTz(timeZone, 1);
  if (date === todayDate) return t("pickup.date.today");
  if (date === tomorrowDate) return t("pickup.date.tomorrow");
  return formatDateInTz(date, timeZone, "EEEE, MMMM d, yyyy", locale);
}

const stateTone: Record<PickupBookingDerivedState, StatusTone> = {
  Scheduled: "primary",
  InProgress: "warning",
  Completed: "success",
  Failed: "danger",
  Cancelled: "default",
};

export function PickupBookingDetailSheet({ bookingId, onClose }: Props) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const { data, isLoading } = useMyPickupBookings();
  const booking = useMemo(
    () => data?.data.find((b) => b.id === bookingId) ?? null,
    [data, bookingId]
  );

  // Notes editor — local copy with save on blur. Track the booking id we
  // initialized for so we re-sync when the user switches to a different
  // booking; the React 19 lint rule discourages setState-in-effect, so we
  // do it during render instead (idiomatic prev-id pattern).
  const [notesDraft, setNotesDraft] = useState(booking?.notes ?? "");
  const [notesDraftBookingId, setNotesDraftBookingId] = useState<
    string | undefined
  >(booking?.id);
  if (booking && booking.id !== notesDraftBookingId) {
    setNotesDraftBookingId(booking.id);
    setNotesDraft(booking.notes ?? "");
  }

  const updateNotes = useUpdatePickupBookingNotes();
  const cancelMutation = useCancelPickupBooking();
  const detachMutation = useDetachShipmentFromPickupBooking();
  const printMutation = usePrintPickupBooking();

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [pendingDetachShipmentId, setPendingDetachShipmentId] = useState<
    string | null
  >(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [newPickup, setNewPickup] = useState<{
    shipmentIds: string[];
  } | null>(null);
  const [blockedAlertOpen, setBlockedAlertOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const isOpen = bookingId !== null;
  const isReadOnly =
    booking?.derivedState !== "Scheduled" &&
    booking?.derivedState !== undefined;

  const handleNotesBlur = async () => {
    if (!booking) return;
    const next = notesDraft.trim() === "" ? null : notesDraft;
    if (next === (booking.notes ?? null)) return;
    try {
      await updateNotes.mutateAsync({ bookingId: booking.id, notes: next });
    } catch {
      toast({
        title: t("pickup.notes.saveFailedTitle"),
        description: t("pickup.notes.saveFailedBody"),
        variant: "destructive",
      });
    }
  };

  const handleCancel = async () => {
    if (!booking) return;
    try {
      const result = await cancelMutation.mutateAsync({
        bookingId: booking.id,
      });
      setConfirmCancelOpen(false);
      onClose();
      const refundCents = result.data.refundRequest?.expectedAmountCents ?? 0;
      const refundLabel =
        refundCents > 0
          ? t("pickup.detail.refundRequested", {
              amount: new Intl.NumberFormat(locale, {
                style: "currency",
                currency: "CAD",
              }).format(refundCents / 100),
            })
          : "";
      toast({
        title: t("pickup.detail.cancelledTitle"),
        description: t("pickup.detail.cancelledDescription", {
          refund: refundLabel,
        }),
      });
    } catch (err) {
      setConfirmCancelOpen(false);
      if (isConflictError(err)) {
        setBlockedAlertOpen(true);
        return;
      }
      toast({
        title: t("pickup.detail.cancelFailedTitle"),
        description: t("pickup.detail.cancelFailedDescription"),
        variant: "destructive",
      });
    }
  };

  const handleDetach = async () => {
    if (!booking || !pendingDetachShipmentId) return;
    try {
      const result = await detachMutation.mutateAsync({
        bookingId: booking.id,
        shipmentId: pendingDetachShipmentId,
      });
      setPendingDetachShipmentId(null);
      const refundCents = result.data.refundRequest?.expectedAmountCents ?? 0;
      const refundLabel =
        refundCents > 0
          ? t("pickup.detail.refundRequestedShort", {
              amount: new Intl.NumberFormat(locale, {
                style: "currency",
                currency: "CAD",
              }).format(refundCents / 100),
            })
          : "";
      if (result.data.booking.cancelledAt) {
        toast({
          title: t("pickup.detail.cancelledTitle"),
          description: t("pickup.detail.autoCancelledDescription", {
            refund: refundLabel,
          }),
        });
        onClose();
      } else {
        toast({
          title: t("pickup.detail.shipmentRemovedTitle"),
          description: t("pickup.detail.shipmentRemovedDescription", {
            refund: refundLabel,
          }),
        });
      }
    } catch (err) {
      setPendingDetachShipmentId(null);
      if (isConflictError(err)) {
        setBlockedAlertOpen(true);
        return;
      }
      toast({
        title: t("pickup.detail.removeFailedTitle"),
        description: t("pickup.notes.saveFailedBody"),
        variant: "destructive",
      });
    }
  };

  const handlePrint = async () => {
    if (!booking) return;
    try {
      await printMutation.print(booking.id, {
        loading: t("pickup.detail.loadingPrintSheet"),
        failed: t("pickup.detail.printFailedTitle"),
      });
    } catch {
      toast({
        title: t("pickup.detail.printFailedTitle"),
        description: t("pickup.notes.saveFailedBody"),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onOpenChange={(v) => !v && onClose()}
        size="full"
        classNames={{
          base: "!max-w-full sm:!max-w-[440px]",
        }}
      >
        <DrawerContent>
          <DrawerHeader className="flex-col items-start gap-1">
            <div className="text-lg font-bold tracking-tight">
              {t("pickup.detail.title")}
            </div>
            <p className="text-tiny text-default-500 font-normal">
              {t("pickup.detail.subtitle")}
            </p>
          </DrawerHeader>
          <DrawerBody className="p-0">
            {isLoading || !booking ? (
              <div className="py-12 flex items-center justify-center text-default-500 gap-2">
                <Spinner size="sm" color="primary" />
                {t("pickup.common.loading")}
              </div>
            ) : (
              <>
                <SheetSection>
                  <div className="flex items-center gap-2.5 mb-3.5">
                    <StatusBadge tone={stateTone[booking.derivedState]}>
                      {t(`pickup.status.${booking.derivedState}`)}
                    </StatusBadge>
                    <span className="text-sm text-default-600">
                      {t("pickup.common.shipmentsAttached", {
                        count: booking.attachedShipmentCount,
                      })}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2.5 mb-3">
                    <div className="p-3 rounded-medium bg-default-50">
                      <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-1">
                        {t("pickup.detail.when")}
                      </div>
                      <div className="text-sm font-semibold text-default-900">
                        {formatDateLong(
                          booking.scheduledDate,
                          booking.dispatchCenter.timeZone,
                          t,
                          locale
                        )}
                      </div>
                      <div className="text-xs text-default-600 mt-0.5">
                        {formatSlotRangeInTz(
                          booking.slot,
                          booking.dispatchCenter.timeZone,
                          booking.scheduledDate.slice(0, 10),
                          locale
                        )}
                      </div>
                    </div>
                    <div className="p-3 rounded-medium bg-default-50">
                      <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-1">
                        {t("pickup.detail.where")}
                      </div>
                      <div className="text-sm font-semibold text-default-900">
                        {booking.pickupAddress.line1}
                      </div>
                      <div className="text-xs text-default-600 mt-0.5">
                        {[
                          booking.pickupAddress.city,
                          booking.pickupAddress.province,
                          booking.pickupAddress.postalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="bordered"
                      size="sm"
                      onPress={handlePrint}
                      isLoading={printMutation.isPending}
                      isDisabled={printMutation.isPending}
                      startContent={
                        !printMutation.isPending ? (
                          <Printer className="h-4 w-4" />
                        ) : null
                      }
                    >
                      {t("pickup.detail.printSheet")}
                    </Button>
                    {!isReadOnly && (
                      <Button
                        variant="bordered"
                        size="sm"
                        onPress={() => setRescheduleOpen(true)}
                      >
                        {t("pickup.detail.reschedule")}
                      </Button>
                    )}
                    {!isReadOnly && (
                      <Button
                        variant="bordered"
                        size="sm"
                        className="ml-auto text-danger-700 ring-1 ring-inset ring-danger-300"
                        onPress={() => setConfirmCancelOpen(true)}
                        isDisabled={cancelMutation.isPending}
                      >
                        {t("pickup.detail.cancelPickup")}
                      </Button>
                    )}
                  </div>
                </SheetSection>

                <SheetSection
                  title={t("pickup.detail.shipmentsTitle")}
                  hint={t("pickup.detail.shipmentsHint")}
                >
                  {booking.shipments.length === 0 ? (
                    <p className="text-sm text-default-500 italic">
                      {t("pickup.detail.noShipments")}
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {booking.shipments.map((s) => (
                        <div
                          key={s.id}
                          className="flex items-center gap-3 p-3 rounded-medium bg-content1 ring-1 ring-inset ring-default-200"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-[13px] font-semibold">
                                SHP-{s.shipmentNumber}
                              </span>
                              {s.trackingNumber && (
                                <span className="text-[11px] text-default-500 font-mono truncate">
                                  · {s.trackingNumber}
                                </span>
                              )}
                            </div>
                            <div className="text-[11.5px] text-default-500 mt-0.5 capitalize">
                              {s.status.replace(/_/g, " ").toLowerCase()}
                            </div>
                          </div>
                          {!isReadOnly && (
                            <button
                              type="button"
                              className="text-xs font-medium text-default-600 hover:text-danger-700 px-2 py-1.5 rounded"
                              disabled={detachMutation.isPending}
                              onClick={() => setPendingDetachShipmentId(s.id)}
                            >
                              {t("pickup.detail.detach")}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {!isReadOnly && (
                    <Button
                      size="sm"
                      variant="light"
                      className="mt-3"
                      startContent={<Plus className="h-3.5 w-3.5" />}
                      onPress={() => setAttachOpen(true)}
                    >
                      {t("pickup.detail.addShipment")}
                    </Button>
                  )}
                </SheetSection>

                <SheetSection
                  title={t("pickup.notes.title")}
                  hint={t("pickup.notes.autoSaveHint")}
                >
                  <Textarea
                    value={notesDraft}
                    onValueChange={(v) =>
                      setNotesDraft(v.slice(0, NOTES_MAX_LENGTH))
                    }
                    onBlur={handleNotesBlur}
                    rows={3}
                    placeholder={t("pickup.notes.placeholder")}
                    isDisabled={isReadOnly}
                    variant="bordered"
                  />
                  <p className="text-[11px] text-default-400 text-right mt-1.5">
                    {notesDraft.length} / {NOTES_MAX_LENGTH}
                  </p>
                </SheetSection>

                {(() => {
                  const rawTotal = booking.shipments.reduce(
                    (sum, s) => sum + (s.pickupPriceCents ?? 0),
                    0
                  );
                  if (rawTotal === 0) return null;
                  const discount = booking.bundleDiscountCents;
                  const tax = booking.taxCents;
                  const net = rawTotal - discount;
                  const totalPaid = net + tax;
                  const fmt = (cents: number) =>
                    new Intl.NumberFormat(locale, {
                      style: "currency",
                      currency: "CAD",
                    }).format(cents / 100);
                  return (
                    <SheetSection title={t("pickup.detail.payment")} noDivider>
                      <div className="rounded-medium bg-content1 ring-1 ring-inset ring-default-200 p-4 text-sm space-y-2">
                        {(discount > 0 || tax > 0) && (
                          <>
                            <div className="flex justify-between text-default-600">
                              <span>
                                {t("pickup.detail.subtotalWithCount", {
                                  count: booking.attachedShipmentCount,
                                })}
                              </span>
                              <span className="font-mono">{fmt(rawTotal)}</span>
                            </div>
                            {discount > 0 && (
                              <div className="flex justify-between text-success-700">
                                <span className="inline-flex items-center gap-1">
                                  {t("pickup.confirm.bundleDiscount")}
                                  <Tooltip
                                    placement="top"
                                    content={t(
                                      "pickup.detail.bundleDiscountTooltip"
                                    )}
                                    classNames={{ content: "max-w-xs" }}
                                  >
                                    <Info className="h-3 w-3 text-success-700/60 cursor-help" />
                                  </Tooltip>
                                </span>
                                <span className="font-mono">
                                  −{fmt(discount)}
                                </span>
                              </div>
                            )}
                            {tax > 0 && (
                              <div className="flex justify-between text-default-600">
                                <span>{t("pickup.common.hst")}</span>
                                <span className="font-mono">{fmt(tax)}</span>
                              </div>
                            )}
                          </>
                        )}
                        <div
                          className={`flex justify-between font-semibold text-default-900 ${
                            discount > 0 || tax > 0
                              ? "border-t border-divider pt-2"
                              : ""
                          }`}
                        >
                          <span>{t("pickup.detail.totalPaid")}</span>
                          <span className="font-mono font-bold">
                            {fmt(totalPaid)}
                          </span>
                        </div>
                      </div>
                    </SheetSection>
                  );
                })()}
              </>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Cancel confirmation */}
      <Modal
        isOpen={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
        size="md"
        placement="center"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-lg font-bold tracking-tight">
                {t("pickup.detail.cancelTitle")}
              </ModalHeader>
              <ModalBody>
                <p className="text-default-600">
                  {booking
                    ? t("pickup.detail.cancelBody", {
                        count: booking.attachedShipmentCount,
                      })
                    : ""}
                </p>
                <div className="rounded-medium bg-warning-50 border border-warning-200 p-3 text-sm text-warning-700">
                  <p className="font-medium">
                    {t("pickup.detail.refundTitle")}
                  </p>
                  <p>{t("pickup.detail.refundBody")}</p>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  {t("pickup.detail.keepPickup")}
                </Button>
                <Button
                  color="danger"
                  onPress={handleCancel}
                  isLoading={cancelMutation.isPending}
                  isDisabled={cancelMutation.isPending}
                >
                  {t("pickup.detail.cancelPickup")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Detach confirmation */}
      <Modal
        isOpen={pendingDetachShipmentId !== null}
        onOpenChange={(v) => !v && setPendingDetachShipmentId(null)}
        size="md"
        placement="center"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-lg font-bold tracking-tight">
                {t("pickup.detail.removeTitle")}
              </ModalHeader>
              <ModalBody>
                <p className="text-default-600">
                  {t("pickup.detail.removeBody")}
                </p>
                <div className="rounded-medium bg-warning-50 border border-warning-200 p-3 text-sm text-warning-700">
                  <p className="font-medium">
                    {t("pickup.detail.refundTitle")}
                  </p>
                  <p>{t("pickup.detail.removeRefundBody")}</p>
                </div>
                {booking && booking.bundleDiscountCents > 0 && (
                  <div className="rounded-medium bg-default-50 border border-divider p-3 text-sm text-default-700">
                    <p className="font-medium">
                      {t("pickup.detail.bundleDiscountTitle")}
                    </p>
                    <p>{t("pickup.detail.bundleDiscountBody")}</p>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  {t("pickup.detail.keepOnPickup")}
                </Button>
                <Button
                  color="danger"
                  onPress={handleDetach}
                  isLoading={detachMutation.isPending}
                  isDisabled={detachMutation.isPending}
                >
                  {t("pickup.detail.remove")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {rescheduleOpen && (
        <PickupRescheduleDialog
          open={rescheduleOpen}
          onOpenChange={setRescheduleOpen}
          bookingId={bookingId}
        />
      )}

      {/* Phase 16c: Attach-shipment modal — booking is non-null when shown */}
      {booking && (
        <AttachToBookingModal
          key={attachOpen ? `open-${booking.id}` : "closed"}
          open={attachOpen}
          onClose={() => setAttachOpen(false)}
          booking={booking}
          onScheduleNew={(shipmentIds) => {
            setAttachOpen(false);
            setNewPickup({ shipmentIds });
          }}
        />
      )}

      {newPickup && (
        <SchedulePickupSheet
          open
          onClose={() => setNewPickup(null)}
          initialShipmentIds={newPickup.shipmentIds}
        />
      )}

      {/* Blocked-by-in-progress alert */}
      <Modal
        isOpen={blockedAlertOpen}
        onOpenChange={setBlockedAlertOpen}
        size="md"
        placement="center"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-lg font-bold tracking-tight">
                {t("pickup.detail.blockedTitle")}
              </ModalHeader>
              <ModalBody>
                <p className="text-default-600">
                  {t("pickup.detail.blockedBodyBeforeEmail")}{" "}
                  <a
                    href="mailto:support@ezx.com"
                    className="text-primary underline"
                  >
                    support@ezx.com
                  </a>{" "}
                  {t("pickup.detail.blockedBodyAfterEmail")}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onPress={onClose}>
                  {t("actions.ok")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
